import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, ExternalLink, Info } from "lucide-react";
import { api, idr, fmtNum } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDataQuery } from "@/hooks/useDataQuery";
import { useDebounce } from "@/hooks/useDebounce";
import LoadingState from "@/components/ui/loading-state";
import { DatePicker } from "@/components/ui/date-picker";
import { PageHeader, TidakPunyaAkses, SearchInput, adaField } from "./_shared";

/**
 * Papan peringkat Skor KOL (Fase 3, brief MKT-03). Satu kunci izin
 * `marketing_kol` untuk seluruh endpoint `/kol-score*` (lihat docstring
 * `backend/routers/marketing/score.py`) — SAMA dengan direktori KOL, jadi
 * tidak ada kunci izin terpisah untuk skor.
 *
 * Kartu skor SENGAJA tidak pernah menampilkan angka 0 untuk KOL yang datanya
 * tipis — backend sudah menandai `insufficient_data`/`grade: null`, di sini
 * hanya diteruskan sebagai badge "Data belum cukup", BUKAN dikonversi ke 0
 * atau huruf (angka palsu bisa dipakai memecat orang, lihat instruksi bos).
 */
export default function KolScoreboard() {
  const { can } = useAuth();
  const bolehLihat = can("marketing_kol", "view");
  const [q, setQ] = useState("");
  const qDebounced = useDebounce(q, 350);
  const [sampai, setSampai] = useState("");

  const { data, loading, error, refetch } = useDataQuery(
    ["marketing", "kol-score-leaderboard", sampai],
    () => api.get("/marketing/kol-score", { params: sampai ? { sampai } : {} }).then((r) => r.data),
    { enabled: bolehLihat },
  );

  const items = useMemo(() => {
    const rows = data?.items || [];
    const t = qDebounced.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter((r) => r.kol_nama?.toLowerCase().includes(t));
  }, [data, qDebounced]);

  const medianAda = adaField(data || {}, "median_cpv_populasi") && data?.median_cpv_populasi != null;

  if (!bolehLihat) {
    return (
      <div className="p-4 md:p-8 space-y-5">
        <PageHeader icon={Trophy} title="Skor KOL" />
        <TidakPunyaAkses label="Skor KOL" testid="mkt-kolscore-tanpa-akses" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="mkt-kolscore-board">
      <PageHeader
        icon={Trophy} title="Skor KOL"
        desc="Peringkat KOL 6 bulan terakhir dari 4 komponen: efisiensi CPV, leads & closing, ROI margin, profesionalisme."
        actions={
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-slate-500">Per tanggal</span>
            <DatePicker value={sampai} onChange={setSampai} placeholder="Hari ini" data-testid="mkt-kolscore-sampai" className="w-36" />
          </div>
        }
      />

      {data?.periode && (
        <p className="text-xs text-slate-500" data-testid="mkt-kolscore-periode">
          Periode penilaian: {data.periode.awal} s/d {data.periode.akhir} (6 bulan terakhir)
          {medianAda && <> · Median CPV populasi: <span className="font-mono font-semibold text-slate-700">{idr(data.median_cpv_populasi)}/view</span></>}
        </p>
      )}

      <SearchInput value={q} onChange={setQ} placeholder="Cari nama KOL..." testid="mkt-kolscore-cari" className="w-64" />

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr className="text-left">
                <th className="px-3 py-2.5 font-semibold w-14">Rank</th>
                <th className="px-3 py-2.5 font-semibold">KOL</th>
                <th className="px-3 py-2.5 font-semibold text-right">Jumlah Deal</th>
                <th className="px-3 py-2.5 font-semibold text-right">Total Spend</th>
                <th className="px-3 py-2.5 font-semibold text-right">Skor</th>
                <th className="px-3 py-2.5 font-semibold">Grade</th>
                <th className="px-3 py-2.5 font-semibold text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <LoadingState loading={loading} error={error} onRetry={refetch} kosong={!loading && !error && items.length === 0}
                            varian="tabel" kolom={7} pesanKosong="Belum ada KOL dengan deal pada periode ini." data-testid="mkt-kolscore-state">
                {items.map((r, idx) => (
                  <tr key={r.kol_id} className="hover:bg-slate-50/60" data-testid={`mkt-kolscore-row-${r.kol_id}`}>
                    <td className="px-3 py-2.5 font-mono text-slate-400">{idx + 1}</td>
                    <td className="px-3 py-2.5">
                      <Link to={`/marketing/kol-score/${r.kol_id}`} className="font-medium text-slate-900 hover:text-emerald-700 hover:underline">
                        {r.kol_nama || r.kol_id.slice(0, 8)}
                      </Link>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">{fmtNum(r.jumlah_deal)}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{idr(r.total_spend)}</td>
                    <td className="px-3 py-2.5 text-right">
                      {r.score == null ? (
                        <span className="text-slate-400 text-xs">Belum ada data</span>
                      ) : (
                        <span className={`font-mono font-semibold ${r.insufficient_data ? "text-slate-500" : "text-slate-900"}`}>
                          {r.score.toFixed(1)}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <GradeBadge grade={r.grade} insufficientData={r.insufficient_data} reason={r.insufficient_data_reason} />
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <Link to={`/marketing/kol-score/${r.kol_id}`} data-testid={`mkt-kolscore-detail-${r.kol_id}`}
                            className="inline-flex items-center gap-1 text-emerald-700 hover:underline text-xs font-medium">
                        Rincian <ExternalLink className="w-3 h-3" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </LoadingState>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const GRADE_META = {
  A: { label: "A", chip: "bg-emerald-100 text-emerald-800" },
  B: { label: "B", chip: "bg-sky-100 text-sky-700" },
  C: { label: "C", chip: "bg-rose-100 text-rose-700" },
};

/** Badge grade — TIDAK PERNAH menampilkan huruf/angka untuk KOL berdata
 *  tipis, hanya chip abu-abu "Data belum cukup" dengan alasan sebagai
 *  tooltip (persis instruksi bos: jangan ubah jadi 0/huruf palsu). */
export function GradeBadge({ grade, insufficientData, reason }) {
  if (insufficientData || !grade) {
    return (
      <span title={reason || undefined}
            className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-medium bg-slate-100 text-slate-500 whitespace-nowrap">
        <Info className="w-3 h-3" /> Data belum cukup
      </span>
    );
  }
  const m = GRADE_META[grade] || { label: grade, chip: "bg-slate-100 text-slate-600" };
  return (
    <span className={`inline-flex items-center text-[11px] px-2 py-0.5 rounded-full font-bold ${m.chip}`}>
      Grade {m.label}
    </span>
  );
}
